/**
 * ConfirmEmailBanner - shown to somebody who has not confirmed their address
 * --------------------------------------------------------------------------
 * Sits at the top of both dashboards. Says why the main buttons are not
 * working yet, and offers the one thing that fixes it.
 *
 * The sibling of GuardianConsentBanner, and written to match it, because they
 * are the same situation from the person's point of view: you have signed up,
 * something is not finished, and nothing on the site is telling you what.
 *
 * WHO SEES IT
 * `emailConfirmationRequired` comes from the server (see publicUser in
 * utils/token.js), which asks the User model. It is deliberately NOT worked
 * out here from role and age - that would be a second copy of a safeguarding
 * rule living in the browser, and the copy is the one that goes stale.
 *
 * The under-18s it must never appear for are exactly the people who cannot
 * act on it: they were never sent the email, because their parent was asked
 * instead. A banner telling them to check an inbox for something that was
 * never sent is worse than no banner.
 */

import { useState } from 'react';
import { MailCheck, Mail, CheckCircle2, Clock } from 'lucide-react';

import { authApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';

import { useI18n } from '../hooks/useI18n';
import { useResendCooldown } from '../hooks/useResendCooldown';

export default function ConfirmEmailBanner() {
  const { t } = useI18n();
  const { user } = useAuth();

  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * Keyed by the account, so two people sharing a laptop do not inherit each
   * other's wait. `user` can be null on the first render, and a hook cannot
   * be called conditionally - hence the fallback rather than an early return
   * above it.
   */
  const cooldown = useResendCooldown(`confirm-email.${user?._id || 'anon'}`);

  if (!user) return null;
  if (!user.emailConfirmationRequired) return null;
  if (user.emailVerified) return null;

  const resend = async () => {
    setBusy(true);
    setError('');
    setSent(false);
    try {
      await authApi.resendConfirmation();
      setSent(true);
      // Started only after the send actually worked. Starting it first would
      // mean a failed send locking somebody out of trying again for two
      // minutes, which is the opposite of what this is for.
      cooldown.start();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="card"
      style={{ borderColor: 'var(--color-amber)', borderWidth: 2, marginBottom: 'var(--space-md)' }}
    >
      <div className="row" style={{ gap: 'var(--space-xs)', alignItems: 'flex-start' }}>
        <MailCheck size={24} className="text-amber" style={{ flexShrink: 0 }} aria-hidden="true" />

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ marginBottom: 6 }}>{t('confirmBanner.title')}</h3>

          <p className="text-muted">
            {t('confirmBanner.body', { email: user.email })}
          </p>

          {/* Said plainly, and said in the same place every time. The old
              version put the server's own sentence here, which was accurate
              and easy to miss - what somebody pressing this needs to know
              first is that it went. */}
          {sent && (
            <div className="alert alert-success" style={{ marginTop: 'var(--space-sm)' }}>
              <CheckCircle2 size={18} aria-hidden="true" />
              <div>
                <strong>{t('resend.sentTitle')}</strong>
                <br />
                <span className="text-sm">{t('resend.sentBody', { email: user.email })}</span>
              </div>
            </div>
          )}
          {error && (
            <div className="alert alert-error" style={{ marginTop: 'var(--space-sm)' }}>
              {error}
            </div>
          )}

          <div className="row" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-sm)' }}>
            <button
              type="button"
              className="btn btn-outline btn-sm btn-inline"
              onClick={resend}
              disabled={busy || !cooldown.ready}
            >
              {cooldown.ready ? <Mail size={16} /> : <Clock size={16} />}
              {busy
                ? t('confirmBanner.sending')
                : cooldown.ready
                  ? t('confirmBanner.resend')
                  : t('resend.waitFor', { time: cooldown.label })}
            </button>
          </div>

          <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
            {t('confirmBanner.spamHint')}
          </p>
        </div>
      </div>
    </div>
  );
}
